import React, { useEffect, useState } from "react";
import { X, CreditCard, Check, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getBilling, startCheckout, deleteTenantData, formatApiError } from "@/auth";

const ORDER = ["trial", "starter", "pro", "enterprise"];

export default function BillingModal({ open, onClose, me, onDataDeleted }) {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState("");
  const [delText, setDelText] = useState("");
  useEffect(() => { if (open) getBilling().then(setB).catch((e) => toast.error(formatApiError(e))); }, [open]);
  if (!open) return null;
  const isOwner = me.role === "owner";

  const upgrade = async (key) => {
    setBusy(key);
    try {
      const r = await startCheckout(`${key}_monthly`);
      if (r.switched) { toast.success(`Plan switched to ${r.plan}`); const nb = await getBilling(); setB(nb); setBusy(""); return; }
      window.location.href = r.checkout_url;
    }
    catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  const wipe = async () => {
    setBusy("delete");
    try { const r = await deleteTenantData(); toast.success(`Deleted ${r.deleted_jobs} customers and all certificates`); onDataDeleted && onDataDeleted(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); setDelText(""); }
  };

  const pct = b ? Math.min(100, Math.round((b.registrations_this_month / b.limits.monthly_registrations) * 100)) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="billing-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl border border-slate-700 bg-[#161f32] shadow-2xl fade-up">
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-slate-100"><CreditCard className="h-5 w-5 text-orange-400" /> Plan &amp; Billing</h2>
          <button data-testid="billing-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto px-6 py-5">
          {!b ? <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
            <>
              <div data-testid="billing-usage" className="mb-6 grid gap-4 rounded-xl border border-slate-800 bg-slate-900/40 p-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Current plan</p>
                  <p className="font-display text-2xl font-bold text-slate-100">{b.limits.label}</p>
                  <p className={`text-xs ${b.plan_status === "past_due" ? "text-rose-400" : "text-emerald-400"}`}>{b.plan_status.replace("_", " ")}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Registrations this month</p>
                  <p className="font-display text-2xl font-bold text-slate-100">{b.registrations_this_month} <span className="text-sm font-normal text-slate-500">/ {b.limits.monthly_registrations}</span></p>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-slate-800"><div className={`h-1.5 rounded-full ${pct >= 90 ? "bg-rose-500" : "bg-orange-500"}`} style={{ width: `${pct}%` }} /></div>
                  {b.overage?.count > 0 ? (
                    <p data-testid="billing-overage" className="mt-1 text-xs text-amber-300">{b.overage.count} over limit · ${b.overage.amount.toFixed(2)} added to next invoice</p>
                  ) : (
                    <p className="mt-1 text-xs text-slate-500">{b.limits.overage ? `$${b.overage_rate.toFixed(2)} per registration over the limit` : "Trial stops at the limit"}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Seats</p>
                  <p className="font-display text-2xl font-bold text-slate-100">{b.seats_used} <span className="text-sm font-normal text-slate-500">/ {b.limits.seats >= 999 ? "∞" : b.limits.seats}</span></p>
                  <p className="text-xs text-slate-500">{b.limits.concurrency} browser{b.limits.concurrency > 1 ? "s" : ""} per portal</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {ORDER.map((k) => {
                  const p = b.plans[k]; const current = b.plan === k;
                  return (
                    <div key={k} data-testid={`plan-card-${k}`} className={`flex flex-col rounded-xl border p-4 ${current ? "border-orange-500/60 bg-orange-500/5" : "border-slate-800 bg-slate-900/30"}`}>
                      <p className="font-display text-lg font-semibold text-slate-100">{p.label}</p>
                      <p className="mt-1 font-display text-3xl font-bold text-slate-100">{p.price ? `$${p.price}` : "Free"}<span className="text-xs font-normal text-slate-500">{p.price ? "/mo" : ""}</span></p>
                      <ul className="mt-3 flex-1 space-y-1.5 text-xs text-slate-300">
                        <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> Up to {p.monthly_registrations.toLocaleString()} registrations{p.price ? " / month" : ""}</li>
                        <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> {p.overage ? `$${b.overage_rate.toFixed(2)} per registration over the limit` : "Stops at the limit"}</li>
                        <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> {p.seats >= 999 ? "Unlimited users" : `${p.seats} user${p.seats > 1 ? "s" : ""}`}</li>
                        <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> {p.concurrency} simultaneous browser{p.concurrency > 1 ? "s" : ""} per portal</li>
                        <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> Trane + Daikin/Goodman/Amana + Rheem/Ruud</li>
                      </ul>
                      {current ? (
                        <span className="mt-4 rounded-lg bg-orange-500/15 py-2 text-center text-xs font-semibold text-orange-300">Current plan</span>
                      ) : p.price ? (
                        <button data-testid={`plan-select-${k}`} disabled={!isOwner || !!busy} onClick={() => upgrade(k)} title={isOwner ? "" : "Only the company owner can change the plan"}
                          className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-600 py-2 text-xs font-semibold text-white hover:bg-orange-500 disabled:opacity-50">
                          {busy === k && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {ORDER.indexOf(k) > ORDER.indexOf(b.plan) ? "Upgrade" : "Switch"}
                        </button>
                      ) : <span className="mt-4 py-2 text-center text-xs text-slate-600">Trial</span>}
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-slate-500">Payments are processed securely by Stripe. {!isOwner && "Only the company owner can change the plan."}</p>

              {isOwner && (
                <div className="mt-8 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-rose-300"><Trash2 className="h-4 w-4" /> Danger zone — delete all company data</p>
                  <p className="mt-1 text-xs text-slate-400">Permanently removes every customer, certificate, screenshot and chat for your company. Users and settings are kept. Type <b>DELETE</b> to enable.</p>
                  <div className="mt-2 flex gap-2">
                    <input data-testid="danger-delete-input" value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="DELETE" className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-rose-500" />
                    <button data-testid="danger-delete-button" disabled={delText !== "DELETE" || busy === "delete"} onClick={wipe} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 disabled:opacity-40">Delete everything</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
